import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resultsAPI } from '../utils/api';
import { Trophy, Horse, Timer, CurrencyDollar } from '@phosphor-icons/react';

const PublishedResults = () => {
  const { showId } = useParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeClass, setActiveClass] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await resultsAPI.getShowPublished(showId);
        setResults(data);
        if (data.length > 0) setActiveClass(data[0].class_id);
      } catch { /* no published results */ }
      finally { setLoading(false); }
    };
    load();
  }, [showId]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0096FF] border-t-transparent rounded-full animate-spin" /></div>;

  if (results.length === 0) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <p className="text-[#9E9E9E] text-lg">No published results for this show yet.</p>
    </div>
  );

  const activeResult = results.find(r => r.class_id === activeClass);
  const showName = results[0]?.show_name || 'Show Results';

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}} data-testid="results-show-name">{showName}</h1>
          <p className="text-[#0096FF] font-semibold text-lg">Official Results</p>
        </div>

        {/* Class Tabs */}
        <div className="flex gap-1 mb-6 bg-[#161616] rounded-lg p-1 overflow-x-auto" data-testid="results-class-tabs">
          {results.map(r => (
            <button
              key={r.class_id}
              onClick={() => setActiveClass(r.class_id)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all inline-flex items-center gap-1.5 ${
                activeClass === r.class_id ? (r.is_sidepot ? 'bg-[#FACC15] text-black' : 'bg-[#0096FF] text-white') : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
              }`}
              data-testid={`results-class-tab-${r.class_id}`}
            >
              {r.class_name}
              {r.is_sidepot && (
                <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${activeClass === r.class_id ? 'bg-black/20 text-black' : 'bg-[#FACC15]/20 text-[#FACC15]'}`}>SP</span>
              )}
            </button>
          ))}
        </div>

        {activeResult && (
          <div className="space-y-6">
            {/* Pool Info */}
            {activeResult.payout_data && (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 flex flex-wrap gap-4" data-testid="results-pool-info">
                <div className="flex items-center gap-2">
                  <CurrencyDollar size={18} className="text-[#34C759]" />
                  <span className="text-[#9E9E9E] text-sm">Total Pool:</span>
                  <span className="text-white font-bold">${activeResult.payout_data.total_pool?.toFixed(0)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Horse size={18} className="text-[#0096FF]" />
                  <span className="text-[#9E9E9E] text-sm">Entries:</span>
                  <span className="text-white font-bold">{activeResult.payout_data.num_entries}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy size={18} className="text-[#FF9500]" />
                  <span className="text-[#9E9E9E] text-sm">Places Paid:</span>
                  <span className="text-white font-bold">{activeResult.payout_data.overall_places_paid}</span>
                </div>
              </div>
            )}

            {/* Division Results with Payouts */}
            {activeResult.division_type && !['NONE', 'OVERALL'].includes(activeResult.division_type) && activeResult.division_standings ? (
              Object.entries(activeResult.division_standings).sort(([a],[b]) => a.localeCompare(b)).map(([divName, riders]) => {
                const divPayout = activeResult.payout_data?.division_payouts?.[divName];
                return (
                  <div key={divName} className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid={`results-div-${divName}`}>
                    <div className="px-4 py-3 bg-[#0096FF]/10 border-b border-[#262626] flex items-center justify-between">
                      <h3 className="text-lg font-bold text-[#0096FF]" style={{fontFamily: 'Outfit'}}>{divName}</h3>
                      {divPayout && (
                        <span className="text-sm text-[#9E9E9E]">{divPayout.entries} entries &bull; Pool: <span className="text-[#34C759] font-bold">${divPayout.pool?.toFixed(0)}</span></span>
                      )}
                    </div>
                    <div className="divide-y divide-[#262626]">
                      {riders.map((rider, idx) => {
                        const payout = divPayout?.payouts?.find(p => p.place === rider.place);
                        return (
                          <div key={idx} className="flex items-center gap-3 px-4 py-3" data-testid={`result-row-${divName}-${idx}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 ${
                              rider.place === 1 ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                              rider.place === 2 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' :
                              rider.place === 3 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' :
                              'bg-[#262626] text-[#9E9E9E]'
                            }`}>{rider.tied ? `T${rider.place}` : rider.place}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold text-sm truncate">{rider.rider_name}</p>
                              {rider.horse_name && <p className="text-[#555] text-xs truncate">{rider.horse_name}</p>}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <div className="flex items-center gap-1 text-sm">
                                <Timer size={14} className="text-[#0096FF]" />
                                <span className="text-white font-mono">{rider.final_time?.toFixed(3) || 'NT'}</span>
                              </div>
                              {payout && payout.amount > 0 && (
                                <span className="px-2 py-0.5 bg-[#34C759]/15 text-[#34C759] rounded text-xs font-bold" data-testid={`payout-amount-${divName}-${idx}`}>
                                  ${payout.amount.toFixed(0)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              /* Overall Results */
              <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid="results-overall">
                <div className="px-4 py-3 bg-[#0096FF]/10 border-b border-[#262626]">
                  <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Results</h3>
                </div>
                <div className="divide-y divide-[#262626]">
                  {activeResult.results.map((rider, idx) => {
                    return (
                      <div key={idx} className="flex items-center gap-3 px-4 py-3" data-testid={`result-row-overall-${idx}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 ${
                          rider.place === 1 ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                          rider.place === 2 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' :
                          rider.place === 3 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' :
                          'bg-[#262626] text-[#9E9E9E]'
                        }`}>{rider.tied ? `T${rider.place}` : rider.place}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate">{rider.rider_name}</p>
                          {rider.horse_name && <p className="text-[#555] text-xs truncate">{rider.horse_name}</p>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="flex items-center gap-1 text-sm">
                            <Timer size={14} className="text-[#0096FF]" />
                            <span className="text-white font-mono">{rider.final_time?.toFixed(3) || 'NT'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Published timestamp */}
            <p className="text-center text-[#555] text-xs">Published {new Date(activeResult.published_at).toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishedResults;
